import {
  type JSX,
  type Ref,
  useEffect,
  useImperativeHandle,
  useState,
} from "react";

import { componentManager, rwc } from "@frontend/bridge/ComponentManager";
import { getElementInternals } from "@frontend/bridge/elementInternals";
import { useIslandHost } from "@frontend/contexts";

import { Collapsible } from "./Collapsible";
import {
  type CollapsibleDataProps,
  collapsibleDataSchema,
} from "./Collapsible.schema";
import { CollapsibleContent } from "./CollapsibleContent";
import {
  COLLAPSIBLE_CONTENT_TAG_NAME,
  COLLAPSIBLE_TAG_NAME,
} from "./constants";

export interface CollapsibleHandle {
  open: () => void;
  close: () => void;
  toggle: () => void;
}

declare module "@frontend/bridge/types" {
  interface ComponentRegistryMap {
    [COLLAPSIBLE_TAG_NAME]: {
      props: CollapsibleDataProps;
      handle: CollapsibleHandle;
    };
  }
  interface InertTagNames {
    [COLLAPSIBLE_CONTENT_TAG_NAME]: true;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    "rwc-collapsible": HTMLElement & CollapsibleHandle;
  }
}

const RWCCollapsibleContent = rwc(COLLAPSIBLE_CONTENT_TAG_NAME);

const RWCCollapsible = rwc(COLLAPSIBLE_TAG_NAME, {
  component: RWCCollapsibleComponent,
  dataSchema: collapsibleDataSchema,
  usesSlots: true,
  readSlotted: readCollapsibleSlotted,
  formAssociated: true,
});

export function readCollapsibleSlotted(
  el: HTMLElement,
): Partial<CollapsibleDataProps> {
  const content = el.querySelector<HTMLElement>(
    `:scope > ${RWCCollapsibleContent.tagName}`,
  );

  content?.setAttribute("slot", "content");

  const onOpenChange: CollapsibleDataProps["onOpenChange"] = (open) => {
    el.toggleAttribute("open", open);

    el.dispatchEvent(
      new CustomEvent("collapsible:change", {
        detail: { open },
        bubbles: true,
      }),
    );
  };

  return { onOpenChange };
}

function RWCCollapsibleComponent({
  ref,
  defaultOpen,
  size,
  variant,
  onOpenChange,
}: Partial<CollapsibleDataProps> & {
  ref?: Ref<CollapsibleHandle>;
}): JSX.Element {
  const host = useIslandHost();

  const [open, setOpen] = useState(defaultOpen ?? false);

  useEffect(() => {
    onOpenChange?.(open);
  }, [open, onOpenChange]);

  useEffect(() => {
    getElementInternals(host)?.setFormValue(open ? "1" : null);
  }, [host, open]);

  useImperativeHandle(
    ref,
    () => ({
      open: () => setOpen(true),
      close: () => setOpen(false),
      toggle: () => setOpen((current) => !current),
    }),
    [],
  );

  return (
    <Collapsible
      size={size}
      variant={variant}
      open={open}
      onOpenChange={setOpen}
    >
      <slot />

      <CollapsibleContent>
        <slot name="content" />
      </CollapsibleContent>
    </Collapsible>
  );
}

componentManager.register(RWCCollapsibleContent, RWCCollapsible);
